import { LOCALE } from "@/core/constants";
import { addDays } from "@/core/format/format";

import type { AnalyticsSummary, SignupRange } from "./analytics.queries";

export const SIGNUP_RANGES: readonly { value: SignupRange; label: string; days: number }[] = [
  { value: "7d", label: "7 days", days: 7 },
  { value: "1m", label: "1 month", days: 30 },
  { value: "6m", label: "6 months", days: 183 },
  { value: "1y", label: "1 year", days: 365 },
];

const isMonth = (value: string | null): value is string => !!value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
export { isMonth };

/** "2026-10" → "October 2026", "October" (name) or "Oct" (short). */
export function formatMonth(month: string, style: "long" | "name" | "short" = "long"): string {
  const [y, m] = month.split("-").map(Number);
  return new Intl.DateTimeFormat(LOCALE, {
    month: style === "short" ? "short" : "long",
    ...(style === "long" ? { year: "numeric" } : {}),
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y!, m! - 1, 1)));
}

/** A month `n` months later (or earlier): "2026-12" + 1 → "2027-01". */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const index = y! * 12 + (m! - 1) + n;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/**
 * Every bar of the new-subscriptions chart, oldest first, with days or months that had none as 0 (the API only
 * sends buckets with plans): days for 7 days and 1 month, months for 6 months and 1 year.
 */
export function signupBars(
  range: SignupRange,
  points: AnalyticsSummary["signups"]["points"],
  today: string,
): { bucket: string; count: number }[] {
  const days = SIGNUP_RANGES.find((r) => r.value === range)!.days;
  const from = addDays(today, -(days - 1));
  const counts = new Map(points.map((p) => [p.bucket, p.count]));
  const buckets: string[] = [];
  if (range === "7d" || range === "1m") {
    for (let day = from; day <= today; day = addDays(day, 1)) buckets.push(day);
  } else {
    for (let month = from.slice(0, 7); month <= today.slice(0, 7); month = addMonths(month, 1)) {
      buckets.push(month);
    }
  }
  return buckets.map((bucket) => ({ bucket, count: counts.get(bucket) ?? 0 }));
}

/** A month's days in weeks, Monday first, with null before the 1st and after the last day. */
export function calendarWeeks(month: string): (string | null)[][] {
  const first = `${month}-01`;
  const lead = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7;
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let day = first; day.startsWith(month); day = addDays(day, 1)) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

type RetentionMonth = AnalyticsSummary["retention"][number];

/**
 * The latest month with a decided retention (some ended plans whose 14 days are over), shown as a key number;
 * or null.
 */
export function latestRetention(retention: AnalyticsSummary["retention"]) {
  return [...retention].reverse().find((r) => r.retentionBps !== null) ?? null;
}

/** What retention means, for the "?" next to it. */
export const RETENTION_HELP =
  "Of the customers whose plan ended in a month, how many bought another plan from the same brand within 14 days of the end. A plan still inside its 14 days is counted once they're over, so a recent month isn't shown worse than it is.";

/** How a month's retention reads: "1 of 3 customers bought again", and how many can still renew. */
export function retentionWords(r: RetentionMonth): { outcome: string; pending: string | null } {
  const decided = r.ended - r.pending;
  return {
    outcome:
      decided > 0
        ? `${r.renewed} of ${decided} ${decided === 1 ? "customer" : "customers"} bought again`
        : "Not decided yet",
    pending: r.pending > 0 ? `${r.pending} can still renew` : null,
  };
}
